import React from 'react'
import { Check } from 'lucide-react'

// The add-exam flow has four internal steps (details, import, processing,
// review) shown as the artboard's three stages. Stages become buttons when the
// caller passes `onSelect`; a stage the caller reports as unreachable stays disabled.
type Stage = { label: string; from: number; to: number }

const STAGES: Stage[] = [
  { label: 'Details', from: 1, to: 1 },
  { label: 'Questions', from: 2, to: 3 },
  { label: 'Review & publish', from: 4, to: 4 },
]

export const STEP_COUNT = 4

const ExamProgressStepper: React.FC<{
  current: number
  /** Number of questions added so far ("{n} added"). */
  questionCount?: number
  /** Jump to a stage (its first step). Without it the stages are not interactive. */
  onSelect?: (stageFirstStep: number) => void
  /** Whether a stage can be jumped to; defaults to every stage. */
  canSelect?: (stageFirstStep: number) => boolean
}> = ({ current, questionCount = 0, onSelect, canSelect }) => {
  const step = Math.max(1, Math.min(STEP_COUNT, current))
  const subs = ['Grade, subject, assignment', `${questionCount} added`, 'Check and go live']

  return (
    <ol aria-label="Create exam steps" className="mb-[22px] flex flex-wrap gap-1.5 rounded-[16px] border border-line bg-surface p-1.5">
      {STAGES.map((s, i) => {
        const isCurrent = step >= s.from && step <= s.to
        const isDone = step > s.to
        const body = (
          <>
            <span aria-hidden className={`grid h-7 w-7 flex-none place-items-center rounded-full font-mono text-xs font-semibold ${
              isCurrent ? 'bg-accent text-navy' : isDone ? 'bg-success-bg text-success' : 'bg-track text-muted-2'}`}>
              {isDone ? <Check size={14} strokeWidth={2.4} /> : i + 1}
            </span>
            <span className="min-w-0">
              {s.label}
              {isDone && <span className="sr-only"> (done)</span>}
              <small className="block text-xs font-medium opacity-80">{subs[i]}</small>
            </span>
          </>
        )
        const tone = isCurrent ? 'bg-navy text-white' : isDone ? 'text-ink' : 'text-muted'
        const box = `flex min-h-[54px] w-full items-center gap-3 rounded-[12px] px-3.5 text-left text-sm font-semibold ${tone}`
        const enabled = !!onSelect && (canSelect ? canSelect(s.from) : true)
        return (
          <li key={s.label} aria-current={isCurrent ? 'step' : undefined} className="flex-[1_1_180px]">
            {onSelect ? (
              <button type="button" disabled={!enabled} onClick={() => onSelect(s.from)}
                className={`${box} border-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${isCurrent ? '' : enabled ? 'hover:bg-panel' : 'cursor-default'}`}>
                {body}
              </button>
            ) : (
              <div className={box}>{body}</div>
            )}
          </li>
        )
      })}
    </ol>
  )
}

export default ExamProgressStepper
